import UpdateButton from '../../../shared/UpdateButton/UpdateButton';
import { Input, InputGroup, Message, toaster, Toggle, Whisper } from 'rsuite';
import GearIcon from '@rsuite/icons/Gear';
import React, { useEffect, useState } from 'react';
import MechanismApi from '../../../../services/Mechanism.api';
import {SharedApi} from '../../../../services/Shared.api';
import StatusControl from '../../../shared/StatusControl/StatusControl';
import { calculateArrowAngles } from '../../ExternalDevices/components/Mechanism/utils/Mechaism.utils';
import TimeControlInput from '../../../shared/TimeControlInput/TimeControlInput';
import { useNavigate } from 'react-router-dom';
import { getTooltip } from '../../../../shared/Tooltip';
import {MechanismData, MechanismParams, MechanismSectionProps, MeteoData} from "../../../../models/Mechanism.model";

const MechanismSection=  ({ clock, deviceId, isRelayEnabled }: MechanismSectionProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [mechanismParams, setMechanismParams] = useState<MechanismParams | null>(null);
  const [mechanism, setMechanism] = useState<MechanismData | null>(null);
  const [hourHandPosition, setHourHandPosition] = useState<number>(0);
  const [minuteHandPosition, setMinuteHandPosition] = useState<number>(0);
  const [meteoData, setMeteoData] = useState<MeteoData>({
    temperature: 0,
    pressure: 0,
    humidity: 0,
  });
  const [movementPermission, setMovementPermission] = useState<boolean>(false);
  const navigate = useNavigate();

  const { hourAngle, minuteAngle } = calculateArrowAngles(
    hourHandPosition,
    minuteHandPosition,
  );

  const getMechanismParams = async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await MechanismApi.getMechanismParams(clock?.id);
      setMechanismParams(result);
      setHourHandPosition(result.arrowPosition?.hour ?? 0);
      setMinuteHandPosition(result.arrowPosition?.minute ?? 0);
      setMovementPermission(result.movementPermission ?? false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Ошибка получения параметров';
      toaster.push(
        <Message type="error" closable>
          {message}
        </Message>,
        { duration: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const getMeteoData = async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await MechanismApi.getMechanismMeteoData(clock?.id);
      setMeteoData(result);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Ошибка получения метeo-данных';
      toaster.push(
        <Message type="error" closable>
          {message}
        </Message>,
        { duration: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const getMechanismData = async (): Promise<void> => {
    try {
      const result = await SharedApi.getDeviceInfo(String(clock?.id));
      setMechanism({
        place: result.place ?? undefined,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Ошибка получения данных механизма';
      toaster.push(
        <Message type="error" closable>
          {message}
        </Message>,
        { duration: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const changeMovementPermission = async (state: boolean): Promise<void> => {
    setLoading(true);
    try {
      const result = await MechanismApi.setMovementPermission(clock?.id, state);
      if (result) {
        await getMechanismParams();
      }
      setMovementPermission(state);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Ошибка изменения разрешения хода';
      toaster.push(
        <Message type="error" closable>
          {message}
        </Message>,
        { duration: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const changeArrowsPosition = async (
    nextHourPosition?: number,
    nextMinutePosition?: number,
  ): Promise<void> => {
    if (!clock?.id) return;

    const hoursPosition = nextHourPosition ?? hourHandPosition;
    const minutesPosition = nextMinutePosition ?? minuteHandPosition;

    setLoading(true);
    try {
      const result = await MechanismApi.setArrowsPosition(
        String(clock.id),
        String(hoursPosition),
        String(minutesPosition),
      );

      if (result) {
        await getMechanismParams();
        toaster.push(
          <Message type="success" closable>
            Положение стрелок сохранено
          </Message>,
          { duration: 3000 },
        );
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Ошибка сохранения положения стрелок';
      toaster.push(
        <Message type="error" closable>
          {message}
        </Message>,
        { duration: 3000 },
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clock?.id) return;
    getMechanismParams().then();
    getMeteoData().then();
    getMechanismData().then();
  }, [clock?.id]);

  return (
    <div className="fieldset-wrapper">
      <div className="legend-title main-legend-title">
        Часовой механизм {clock?.model} {clock?.address}
      </div>
      <section className="primary-clock">
        <div className="row">
          <div className="col-md-12 text-end">
            <UpdateButton loading={loading} onRefresh={getMechanismParams} />
          </div>
        </div>

        <div className="row align-items-center" style={{ padding: '10px', paddingTop: '0px' }}>
          <div className="col-md-12 text-center d-flex align-items-center p-0 ps-1">
            <Toggle
              className="me-2"
              size="sm"
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={movementPermission}
              onChange={changeMovementPermission}
              loading={loading}
              disabled={loading}
            />
            <p>Вкл. ход часового механизма</p>
          </div>
          <div className="col-md-12 text-center d-flex align-items-center p-0">
            <StatusControl
              status={mechanismParams?.autoMode ? 'online' : 'offline'}
            />
            <p>Вкл. автономный ход часового механизма</p>
          </div>
        </div>

        <div className="row" style={{ padding: '10px', paddingTop: '0px' }}>
          <div className="col-md-4">
            <div className="clock mb-2" style={{ position: 'relative' }}>
              <div className={isRelayEnabled ? 'clock-background' : undefined} />
              <img
                className="clock-face"
                src="/clock-face.svg"
                alt="Циферблат"
              />
              <img
                className="clock-hand hour-hand"
                src="/hour-hand.svg"
                alt="Часовая стрелка"
                style={{ transform: `rotate(${hourAngle}deg)` }}
              />
              <img
                className="clock-hand minute-hand"
                src="/minute-hand.svg"
                alt="Минутная стрелка"
                style={{ transform: `rotate(${minuteAngle}deg)` }}
              />
            </div>
          </div>
          <div className="col-md-8">
            <TimeControlInput
              hour={hourHandPosition}
              minute={minuteHandPosition}
              onTimeChanged={(hours: number, minutes: number) => {
                setHourHandPosition(hours);
                setMinuteHandPosition(minutes);
              }}
              onSave={changeArrowsPosition}
              loading={loading}
              autonomousEnabled={!!mechanismParams?.autoMode}
              movementPermission={movementPermission}
            />
            <InputGroup className="mb-2">
              <InputGroup.Addon>Кол. полных оборотов мин. стрелки</InputGroup.Addon>
              <Input
                size="md"
                readOnly
                value={mechanismParams?.minuteHandsFullTurnCounter ?? ''}
              />
            </InputGroup>
            <InputGroup className="mb-2">
              <InputGroup.Addon>Кол. полных оборотов час. стрелки</InputGroup.Addon>
              <Input
                size="md"
                readOnly
                value={mechanismParams?.hourHandsFullTurnCounter ?? ''}
              />
            </InputGroup>
          </div>
        </div>

        <div className="row" style={{ padding: '10px', paddingTop: 0 }}>
          <div className="col-md-4">
            <InputGroup>
              <InputGroup.Addon>Темп.</InputGroup.Addon>
              <Input
                readOnly
                size="md"
                value={meteoData?.temperature ?? ''}
              />
            </InputGroup>
          </div>
          <div className="col-md-4">
            <InputGroup>
              <InputGroup.Addon>Давл.</InputGroup.Addon>
              <Input
                readOnly
                size="md"
                value={meteoData?.pressure ?? ''}
              />
            </InputGroup>
          </div>
          <div className="col-md-4">
            <InputGroup style={{ width: '100%' }}>
              <InputGroup.Addon>Влажн.</InputGroup.Addon>
              <Input
                readOnly
                size="md"
                value={meteoData?.humidity ?? ''}
              />
            </InputGroup>
          </div>
        </div>

        <div className="row" style={{ padding: '10px', paddingTop: '0px' }}>
          <div className="col-md-12">
            <InputGroup>
              <InputGroup.Addon>Место установки</InputGroup.Addon>
              <Input
                size="md"
                readOnly
                value={mechanism?.place ?? ''}
              />
            </InputGroup>
          </div>
        </div>

        <div className="settings">
          <a
            className="settings-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/devices/${deviceId}?tab=devices&externalId=${clock?.id}`);
            }}
          >
            <Whisper
              placement="topStart"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('Перейти в настройки')}
            >
              <GearIcon className="gear-icon" />
            </Whisper>
          </a>
        </div>
      </section>
    </div>
  );
};

export default MechanismSection;
